import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdaptStepIndicator from "../../components/patterns/AdaptStepIndicator";
import ScriptOutputView from "../../components/script/ScriptOutputView";
import EditScriptModal from "../../components/modals/EditScriptModal";
import { brandsService } from "../../services/brands";

/**
 * Script view step of the Adapt flow. Shows script details via shared ScriptOutputView.
 */
const AdaptScript = ({
  baseUrlSlash,
  script,
  brand,
  onScriptRegenerated,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSceneIndex, setEditSceneIndex] = useState(null);
  const rootRef = useRef(null);

  const scriptHash = script?.hash;
  const scriptBasePath = scriptHash
    ? `${baseUrlSlash}script/${encodeURIComponent(scriptHash)}/`
    : null;
  const videosPath = scriptBasePath ? `${scriptBasePath}videos/` : null;
  const showVideos = Boolean(
    scriptHash && location.pathname.includes(`/script/${encodeURIComponent(scriptHash)}/videos`)
  );
  const showCreateOnMount = new URLSearchParams(location.search).get("create") === "1";

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let scrollParent = el.parentElement;
    while (scrollParent) {
      const style = getComputedStyle(scrollParent);
      const overflowY = style.overflowY || style.overflow;
      if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
        scrollParent.scrollTop = 0;
        break;
      }
      scrollParent = scrollParent.parentElement;
    }
  }, []);

  const handleEditSuccess = (newScript) => {
    setEditModalOpen(false);
    setEditSceneIndex(null);
    onScriptRegenerated?.(newScript);
  };

  const handleEditScene = (scene, index) => {
    setEditSceneIndex(index);
    setEditModalOpen(true);
  };

  const goToScript = () => {
    if (!scriptBasePath) return;
    navigate({ pathname: scriptBasePath, search: "" });
  };

  const goToVideos = (create = false) => {
    if (!videosPath) return;
    navigate({
      pathname: videosPath,
      search: create ? "?create=1" : "",
    });
  };

  const handleOpenVideos = () => {
    if (showVideos) {
      goToVideos(true);
      return;
    }
    goToVideos(!script?.video_count);
  };

  const clearCreateParam = () => {
    if (new URLSearchParams(location.search).get("create") === "1") {
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    }
  };

  const refreshScript = () => {
    if (!scriptHash) return;
    brandsService.getScript(
      scriptHash,
      (response) => {
        const nextScript = response?.data;
        if (nextScript) onScriptRegenerated?.(nextScript);
      },
      () => {}
    );
  };

  const editScene = editSceneIndex != null && script?.script_data?.scenes?.[editSceneIndex]
    ? script.script_data.scenes[editSceneIndex]
    : null;

  return (
    <div ref={rootRef} className="space-y-4">
      <AdaptStepIndicator
        baseUrlSlash={baseUrlSlash}
        currentStep="write_script"
        brand={brand}
      />
      <ScriptOutputView
        script={script}
        loading={false}
        error={null}
        showClose={false}
        onEdit={() => { setEditSceneIndex(null); setEditModalOpen(true); }}
        onEditScene={handleEditScene}
        onOpenVideos={handleOpenVideos}
        onOpenScript={goToScript}
        showVideos={showVideos}
        showCreateOnMount={showCreateOnMount}
        onVideoCreated={refreshScript}
        onClearCreateParam={clearCreateParam}
        videoCount={script?.video_count || 0}
      />
      {editModalOpen && script && (
        <EditScriptModal
          script={script}
          sceneIndex={editSceneIndex}
          scene={editScene}
          onClose={() => { setEditModalOpen(false); setEditSceneIndex(null); }}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default AdaptScript;
